import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';
import exerciseService, {
    Exercise,
    SpeakingExercise,
    ExerciseFilters,
    PaginationParams,
    ExerciseSubmission,
    SpeakingSubmission,
} from '@/services/exerciseService';
import {
    fetchExercisesRequest,
    fetchExercisesSuccess,
    fetchExercisesFailure,
    fetchExerciseByIdRequest,
    fetchExerciseByIdSuccess,
    fetchExerciseByIdFailure,
    submitExerciseRequest,
    submitExerciseSuccess,
    submitExerciseFailure,
    fetchSpeakingExercisesRequest,
    fetchSpeakingExercisesSuccess,
    fetchSpeakingExercisesFailure,
    fetchSpeakingExerciseByIdRequest,
    fetchSpeakingExerciseByIdSuccess,
    fetchSpeakingExerciseByIdFailure,
    submitSpeakingExerciseRequest,
    submitSpeakingExerciseSuccess,
    submitSpeakingExerciseFailure,
    toggleSpeakingFavoriteRequest,
    toggleSpeakingFavoriteSuccess,
    toggleSpeakingFavoriteFailure,
    fetchExerciseResultsRequest,
    fetchExerciseResultsSuccess,
    fetchExerciseResultsFailure,
    fetchSpeakingResultsRequest,
    fetchSpeakingResultsSuccess,
    fetchSpeakingResultsFailure,
    fetchAssessmentQuestionsRequest,
    fetchAssessmentQuestionsSuccess,
    fetchAssessmentQuestionsFailure,
    submitAssessmentRequest,
    submitAssessmentSuccess,
    submitAssessmentFailure,
    fetchExerciseCategoriesRequest,
    fetchExerciseCategoriesSuccess,
    fetchExerciseCategoriesFailure,
    fetchExerciseTypesRequest,
    fetchExerciseTypesSuccess,
    fetchExerciseTypesFailure,
} from '../slices/exerciseSlice';

// Worker Sagas
function* fetchExercisesWorker(
    action: PayloadAction<{ filters?: ExerciseFilters; pagination?: PaginationParams }>,
): SagaIterator {
    try {
        const { filters, pagination } = action.payload;
        const response = yield call(exerciseService.getExercises, filters, pagination);
        yield put(fetchExercisesSuccess(response));
    } catch (error: any) {
        yield put(fetchExercisesFailure(error.response?.data?.message || 'Failed to fetch exercises'));
    }
}

function* fetchExerciseByIdWorker(action: PayloadAction<string>): SagaIterator {
    try {
        const exercise = yield call(exerciseService.getExerciseById, action.payload);
        yield put(fetchExerciseByIdSuccess(exercise));
    } catch (error: any) {
        yield put(fetchExerciseByIdFailure(error.response?.data?.message || 'Failed to fetch exercise'));
    }
}

function* submitExerciseWorker(action: PayloadAction<ExerciseSubmission>): SagaIterator {
    try {
        const result = yield call(exerciseService.submitExercise, action.payload);
        yield put(submitExerciseSuccess(result));
    } catch (error: any) {
        yield put(submitExerciseFailure(error.response?.data?.message || 'Failed to submit exercise'));
    }
}

function* fetchSpeakingExercisesWorker(
    action: PayloadAction<{ filters?: ExerciseFilters; pagination?: PaginationParams }>,
): SagaIterator {
    try {
        const { filters, pagination } = action.payload;
        const response = yield call(exerciseService.getSpeakingExercises, filters, pagination);
        yield put(fetchSpeakingExercisesSuccess(response));
    } catch (error: any) {
        yield put(fetchSpeakingExercisesFailure(error.response?.data?.message || 'Failed to fetch speaking exercises'));
    }
}

function* fetchSpeakingExerciseByIdWorker(action: PayloadAction<string>): SagaIterator {
    try {
        const exercise = yield call(exerciseService.getSpeakingExerciseById, action.payload);
        yield put(fetchSpeakingExerciseByIdSuccess(exercise));
    } catch (error: any) {
        yield put(
            fetchSpeakingExerciseByIdFailure(error.response?.data?.message || 'Failed to fetch speaking exercise'),
        );
    }
}

function* submitSpeakingExerciseWorker(action: PayloadAction<SpeakingSubmission>): SagaIterator {
    try {
        const result = yield call(exerciseService.submitSpeakingExercise, action.payload);
        yield put(submitSpeakingExerciseSuccess(result));
    } catch (error: any) {
        yield put(submitSpeakingExerciseFailure(error.response?.data?.message || 'Failed to submit speaking exercise'));
    }
}

function* toggleSpeakingFavoriteWorker(action: PayloadAction<string>): SagaIterator {
    try {
        const exercise = yield call(exerciseService.toggleSpeakingFavorite, action.payload);
        yield put(toggleSpeakingFavoriteSuccess(exercise));
    } catch (error: any) {
        yield put(toggleSpeakingFavoriteFailure(error.response?.data?.message || 'Failed to toggle favorite'));
    }
}

function* fetchExerciseResultsWorker(
    action: PayloadAction<{ exerciseId?: string; pagination?: PaginationParams }>,
): SagaIterator {
    try {
        const { exerciseId, pagination } = action.payload;
        const response = yield call(exerciseService.getExerciseResults, exerciseId, pagination);
        yield put(fetchExerciseResultsSuccess(response));
    } catch (error: any) {
        yield put(fetchExerciseResultsFailure(error.response?.data?.message || 'Failed to fetch exercise results'));
    }
}

function* fetchSpeakingResultsWorker(
    action: PayloadAction<{ exerciseId?: string; pagination?: PaginationParams }>,
): SagaIterator {
    try {
        const { exerciseId, pagination } = action.payload;
        const response = yield call(exerciseService.getSpeakingResults, exerciseId, pagination);
        yield put(fetchSpeakingResultsSuccess(response));
    } catch (error: any) {
        yield put(fetchSpeakingResultsFailure(error.response?.data?.message || 'Failed to fetch speaking results'));
    }
}

function* fetchAssessmentQuestionsWorker(): SagaIterator {
    try {
        const questions = yield call(exerciseService.getAssessmentQuestions);
        yield put(fetchAssessmentQuestionsSuccess(questions));
    } catch (error: any) {
        yield put(
            fetchAssessmentQuestionsFailure(error.response?.data?.message || 'Failed to fetch assessment questions'),
        );
    }
}

function* submitAssessmentWorker(
    action: PayloadAction<{ questionId: string; answer: string | number }[]>,
): SagaIterator {
    try {
        const result = yield call(exerciseService.submitAssessment, action.payload);
        yield put(submitAssessmentSuccess(result));
    } catch (error: any) {
        yield put(submitAssessmentFailure(error.response?.data?.message || 'Failed to submit assessment'));
    }
}

function* fetchExerciseCategoriesWorker(): SagaIterator {
    try {
        const categories = yield call(exerciseService.getExerciseCategories);
        yield put(fetchExerciseCategoriesSuccess(categories));
    } catch (error: any) {
        yield put(fetchExerciseCategoriesFailure(error.response?.data?.message || 'Failed to fetch categories'));
    }
}

function* fetchExerciseTypesWorker(): SagaIterator {
    try {
        const types = yield call(exerciseService.getExerciseTypes);
        yield put(fetchExerciseTypesSuccess(types));
    } catch (error: any) {
        yield put(fetchExerciseTypesFailure(error.response?.data?.message || 'Failed to fetch types'));
    }
}

// Watcher Sagas
function* watchExercise(): SagaIterator {
    yield takeLatest(fetchExercisesRequest.type, fetchExercisesWorker);
    yield takeLatest(fetchExerciseByIdRequest.type, fetchExerciseByIdWorker);
    yield takeLatest(submitExerciseRequest.type, submitExerciseWorker);
    yield takeLatest(fetchSpeakingExercisesRequest.type, fetchSpeakingExercisesWorker);
    yield takeLatest(fetchSpeakingExerciseByIdRequest.type, fetchSpeakingExerciseByIdWorker);
    yield takeLatest(submitSpeakingExerciseRequest.type, submitSpeakingExerciseWorker);
    yield takeLatest(toggleSpeakingFavoriteRequest.type, toggleSpeakingFavoriteWorker);
    yield takeLatest(fetchExerciseResultsRequest.type, fetchExerciseResultsWorker);
    yield takeLatest(fetchSpeakingResultsRequest.type, fetchSpeakingResultsWorker);
    yield takeLatest(fetchAssessmentQuestionsRequest.type, fetchAssessmentQuestionsWorker);
    yield takeLatest(submitAssessmentRequest.type, submitAssessmentWorker);
    yield takeLatest(fetchExerciseCategoriesRequest.type, fetchExerciseCategoriesWorker);
    yield takeLatest(fetchExerciseTypesRequest.type, fetchExerciseTypesWorker);
}

export default watchExercise;
